/**
 * The popup a menu opens — `KLQ-20260825-006` §C.
 *
 * **Why this file exists at all.** `Menu.tsx` was written first, in the UX cleanup, as a
 * trigger (`⋯`) that opens a list. Its own module header says what has to happen when this
 * spec lands: *"its `ContextMenu` is a second way to open this popup and this item model —
 * never a second menu component."* This is that split. Everything that is *the list* — the
 * portal, the placement, the arrow keys, the submenu, the item model — lives here; `Menu.tsx`
 * keeps the trigger and nothing else, and `ContextMenu.tsx` opens the same list at a point.
 *
 * THREE THINGS THAT MOVED, AND ONE THAT DID NOT
 *
 * 1. **An anchor is a rect or a point.** A trigger has a box to line up with; a right-click
 *    has neither box nor element — the pointer is the whole anchor. Both collapse to "put a
 *    fixed box here, and flip it when the viewport is in the way", so they are one function
 *    with two inputs rather than two positioners that will drift apart. That function now
 *    lives in `placement.ts`, because the grid's cell picker became its second caller
 *    (`KLQ-20260828-001` §B.3) and the drift this point warns about is between *components*,
 *    not only between anchors.
 * 2. **`hidden` is part of the item, not of the call site** (§C.2). An action states its own
 *    applicability beside its own label — `hidden: subject.length === 0` — instead of the
 *    constructor growing a filter that has to be read next to the list to understand it. The
 *    pruning of the separators that a hidden item strands happens here, once, so no
 *    constructor has to think about a rule between two groups it did not draw.
 * 3. **One level of submenu, and it is the same component.** *Priority ▸* is a list of five
 *    values; drawing it as five top-level items buries the four actions people actually came
 *    for. The submenu is `<MenuPopup>` again, anchored to the item's own rect — recursion,
 *    not a second implementation — and the depth stops at one because a menu that needs two
 *    is a drawer.
 *
 * What did not move: **choosing closes before it acts**, because an action that opens a
 * drawer must not fight a popup that is still trying to give focus back to a trigger the
 * caller has just unmounted. That is `Menu.tsx`'s decision 4 and it is now enforced here, for
 * both doors at once.
 */

import { ChevronRight } from "lucide-react";
import type { KeyboardEvent, ReactNode } from "react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import styles from "./Menu.module.css";
import type { Anchor, Placement } from "./placement";
import { GAP, place } from "./placement";

/** A destructive action is drawn in danger ink. It is still only an item — no confirmation
    lives here; a screen that needs one owns that beat itself. */
export type MenuTone = "default" | "danger";

export interface MenuAction {
  readonly id: string;
  readonly label: string;
  /** Absent on an item that only opens a submenu, and on one that is `disabled`. */
  readonly onSelect?: (() => void) | undefined;
  /** Lucide, 1.5px stroke — the same drawing hand as `Button`. */
  readonly icon?: ReactNode | undefined;
  readonly tone?: MenuTone | undefined;
  /** Kept in the list, greyed. A capability that disappears when it is unavailable teaches
      nobody that it exists; one that is visibly not offered right now does. */
  readonly disabled?: boolean | undefined;
  /**
   * Not drawn at all — §C.2. The difference from `disabled` is the difference between *"this
   * does not apply here"* and *"this applies here but not right now"*, and the second one owes
   * the person a `reason`.
   */
  readonly hidden?: boolean | undefined;
  /** Why it is inert, in the surface's words. A greyed item with no explanation is the
      interaction people file bugs about. */
  readonly reason?: string | undefined;
  /** A hint such as `⌘C`. The binding itself lives with the action, never here — a menu that
      owned its shortcuts would be a second keymap that agrees with the first by luck. */
  readonly shortcut?: string | undefined;
  /** One level, no more (§C.2). Items in here may not carry `items` of their own. */
  readonly items?: readonly MenuItem[] | undefined;
}

/** A rule between groups. `id` exists only so React has a key. */
export interface MenuSeparator {
  readonly id: string;
  readonly separator: true;
}

export type MenuItem = MenuAction | MenuSeparator;

export function isSeparator(item: MenuItem): item is MenuSeparator {
  return "separator" in item;
}

/**
 * What is actually drawn: hidden items gone, and the separators they stranded gone with them.
 *
 * A constructor lists its groups once and lets applicability do the rest; without this, a
 * selection of closed tasks would draw a menu that is three rules and one item.
 */
export function drawnItems(items: readonly MenuItem[]): MenuItem[] {
  const kept = items.filter(
    (item) => isSeparator(item) || item.hidden !== true,
  );
  const out: MenuItem[] = [];
  for (const item of kept) {
    const last = out[out.length - 1];
    // No leading rule, and never two in a row.
    if (isSeparator(item) && (last === undefined || isSeparator(last))) continue;
    out.push(item);
  }
  const tail = out[out.length - 1];
  if (tail !== undefined && isSeparator(tail)) out.pop();
  return out;
}

/** Every action a menu would draw — what a caller asks before offering the door at all. */
export function drawnActions(items: readonly MenuItem[]): MenuAction[] {
  return drawnItems(items).filter((item): item is MenuAction => !isSeparator(item));
}

/**
 * Where the popup goes — the arithmetic lives in `placement.ts`, shared with the grid's cell
 * picker so that two floating boxes cannot disagree about what a viewport edge means.
 */
export type MenuAnchor = Anchor;

/**
 * Why a menu closed, which is not the same question as *"should focus go back?"*.
 *
 * This was a boolean called `restoreFocus`, and it was overloaded: a **submenu** read `true` as
 * *"give me the parent item back"* and `false` as *"the whole menu is finished"*, while the
 * **root** owner read `true` as *"focus the trigger"*. Three states in two values, and the
 * collision was live — choosing an item inside a submenu either stranded focus on `<body>` or
 * left the parent list standing, depending on which of the two readings you fixed.
 *
 *   - `dismissed` — Escape, or ArrowLeft out of a submenu. The person is stepping *back*: a
 *     submenu hands focus to its parent item, the root hands it to whatever opened the menu.
 *   - `chosen` — an item ran. The act is over at every level, and focus belongs back at the
 *     trigger, because the trigger is where the person was standing.
 *   - `moved-on` — Tab, a click elsewhere, a scroll. Focus is already going somewhere the
 *     person chose, and pulling it back would be the menu arguing with them.
 */
export type MenuCloseReason = "dismissed" | "chosen" | "moved-on";

export interface MenuPopupProps {
  /** The menu's accessible name — already translated. This file has no copy of its own. */
  readonly label: string;
  /** What a trigger's `aria-controls` points at. Absent for a menu nothing triggered. */
  readonly id?: string | undefined;
  readonly items: readonly MenuItem[];
  readonly anchor: MenuAnchor;
  /** Which item takes focus when it mounts: the first, or the last when opened with ArrowUp. */
  readonly entry?: "first" | "last" | undefined;
  /** Why the menu is closing — see `MenuCloseReason`. */
  readonly onClose: (reason: MenuCloseReason) => void;
  /** A submenu leaves outside-click and viewport-move handling to the popup that opened it. */
  readonly nested?: boolean | undefined;
  /**
   * Whether the list takes focus when it appears. A submenu opened by *hovering* its parent
   * must not: the pointer said "show me", not "I am working in here", and pulling focus on
   * hover would take ArrowDown away from the list somebody is actually reading. Opened by a
   * click or by ArrowRight, it does.
   */
  readonly autoFocus?: boolean | undefined;
}

export function MenuPopup({
  label,
  id,
  items,
  anchor,
  entry = "first",
  onClose,
  nested = false,
  autoFocus = true,
}: MenuPopupProps) {
  const popup = useRef<HTMLDivElement>(null);
  /** Null until the layout effect has measured. The popup is invisible until then, so the
      first painted frame is already in the right place. */
  const [placement, setPlacement] = useState<Placement | null>(null);
  /** The open submenu: which item, the box to hang it off, and whether it may take focus. */
  const [sub, setSub] = useState<{
    id: string;
    rect: DOMRect;
    focus: boolean;
  } | null>(null);

  const drawn = drawnItems(items);

  useLayoutEffect(() => {
    const box = popup.current?.getBoundingClientRect();
    if (box === undefined) return;
    const next = place(anchor, box);
    // Same numbers, same object: the anchor is a fresh literal on every render of the owner,
    // so an unguarded `setPlacement` here is a render loop rather than a measurement.
    setPlacement((current) =>
      current !== null && current.top === next.top && current.left === next.left
        ? current
        : next,
    );
  }, [anchor, drawn.length]);

  useEffect(() => {
    if (nested) return;

    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      // Any popup of this menu counts as inside — a submenu is portalled to `body` too, so a
      // `contains` check on this element alone would close the parent the moment somebody
      // reached into the list it opened.
      if (target.closest("[data-menu-popup]") !== null) return;
      onClose("moved-on");
    };
    // Capture, so a scroll anywhere — including inside a card or a drawer body — is seen.
    const onViewportMove = (): void => {
      onClose("moved-on");
    };

    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("scroll", onViewportMove, true);
    window.addEventListener("resize", onViewportMove);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("scroll", onViewportMove, true);
      window.removeEventListener("resize", onViewportMove);
    };
  }, [nested, onClose]);

  /** The reachable items, in the order they are drawn. Disabled ones are skipped by the arrow
      keys — they are readable, not reachable. */
  const focusable = useCallback((): HTMLButtonElement[] => {
    const nodes = popup.current?.querySelectorAll<HTMLButtonElement>(
      ":scope > [data-menu-item]",
    );
    return nodes === undefined ? [] : [...nodes].filter((node) => !node.disabled);
  }, []);

  const focusAt = useCallback(
    (index: number) => {
      const nodes = focusable();
      if (nodes.length === 0) return;
      // Wraps: the list is short, and somebody holding ArrowDown expects to come round again.
      const wrapped = ((index % nodes.length) + nodes.length) % nodes.length;
      nodes[wrapped]?.focus();
    },
    [focusable],
  );

  // Focus enters the list once it has been placed. Before that the popup is invisible, and
  // focusing something nobody can see is how a screen reader ends up describing a ghost.
  useEffect(() => {
    if (placement === null || !autoFocus) return;
    focusAt(entry === "last" ? -1 : 0);
  }, [placement, entry, autoFocus, focusAt]);

  function choose(action: MenuAction): void {
    if (action.items !== undefined) return;
    /**
     * Gone first, then acted on — **and focus goes back where the menu was opened from**.
     *
     * Closing as `moved-on` here was the difference between a menu a mouse user finishes and a
     * menu a keyboard user falls out of: running an item dropped focus onto `<body>`, so the
     * next Tab started at the top of the document rather than at the row the person was
     * standing on. `KLQ-20260825-006` §I names this exact step — *"arrows and Enter to run
     * Assign, focus returns to the row"* — and it was the one clause of that sentence that was
     * not true. Found 2026-09-09 by `e2e/bulk-actions-keyboard.spec.ts`.
     *
     * Restoring **before** the action runs, not after: an action that moves focus itself — a
     * navigation, a drawer, a confirmation that claims it — then wins, which is the order that
     * makes this safe rather than bossy.
     */
    onClose("chosen");
    action.onSelect?.();
  }

  function openSub(
    action: MenuAction,
    element: HTMLElement,
    focus: boolean,
  ): void {
    if (action.items === undefined) return;
    setSub({ id: action.id, rect: element.getBoundingClientRect(), focus });
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    const nodes = focusable();
    const active = document.activeElement;
    const here = active instanceof HTMLButtonElement ? nodes.indexOf(active) : -1;
    const item = drawn.filter((one): one is MenuAction => !isSeparator(one))[here];

    switch (event.key) {
      case "Escape":
        event.preventDefault();
        event.stopPropagation();
        onClose("dismissed");
        return;
      case "ArrowDown":
        event.preventDefault();
        event.stopPropagation();
        focusAt(here + 1);
        return;
      case "ArrowUp":
        event.preventDefault();
        event.stopPropagation();
        focusAt(here - 1);
        return;
      case "ArrowRight":
        if (item?.items !== undefined && active instanceof HTMLElement) {
          event.preventDefault();
          event.stopPropagation();
          openSub(item, active, true);
        }
        return;
      case "ArrowLeft":
        // Leaving a submenu is closing it; in the top-level list the key means nothing, and
        // swallowing it there would take ArrowLeft away from whatever is behind the menu.
        if (nested) {
          event.preventDefault();
          event.stopPropagation();
          onClose("dismissed");
        }
        return;
      case "Home":
        event.preventDefault();
        event.stopPropagation();
        focusAt(0);
        return;
      case "End":
        event.preventDefault();
        event.stopPropagation();
        focusAt(-1);
        return;
      case "Tab":
        // Tab leaves the menu rather than cycling inside it. Focus is not restored: the
        // person asked to move on, and the browser is already moving.
        onClose("moved-on");
        return;
      default:
        return;
    }
  }

  /** The open submenu, resolved: the item it belongs to, and the box it hangs off. An item
      that stopped being drawn while its submenu was open simply has none. */
  const branch = ((): {
    action: MenuAction;
    rect: DOMRect;
    focus: boolean;
  } | null => {
    if (sub === null) return null;
    const action = drawn.find(
      (one): one is MenuAction => !isSeparator(one) && one.id === sub.id,
    );
    return action?.items === undefined
      ? null
      : { action, rect: sub.rect, focus: sub.focus };
  })();

  return createPortal(
    <>
      <div
        ref={popup}
        id={id}
        role="menu"
        aria-label={label}
        data-menu-popup=""
        className={[styles.popup, placement === null ? styles.measuring : ""]
          .filter(Boolean)
          .join(" ")}
        style={placement === null ? undefined : { top: placement.top, left: placement.left }}
        onKeyDown={onKeyDown}
      >
        {drawn.map((item) =>
          isSeparator(item) ? (
            <div key={item.id} className={styles.separator} role="separator" />
          ) : (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              data-menu-item=""
              aria-haspopup={item.items === undefined ? undefined : "menu"}
              aria-expanded={item.items === undefined ? undefined : sub?.id === item.id}
              title={item.reason}
              className={[styles.item, item.tone === "danger" ? styles.danger : ""]
                .filter(Boolean)
                .join(" ")}
              disabled={item.disabled === true}
              // Focus follows the pointer, so hovering and arrowing cannot disagree about
              // which item is the current one — and moving onto a plain item closes whatever
              // submenu was open, which is what leaving it means.
              onMouseEnter={(event) => {
                event.currentTarget.focus();
                if (item.items === undefined) setSub(null);
                else openSub(item, event.currentTarget, false);
              }}
              onClick={(event) => {
                if (item.items === undefined) choose(item);
                else openSub(item, event.currentTarget, true);
              }}
            >
              <span className={styles.glyph} aria-hidden>
                {item.icon}
              </span>
              <span className={styles.label}>{item.label}</span>
              {item.shortcut === undefined ? null : (
                <span className={styles.shortcut} aria-hidden>
                  {item.shortcut}
                </span>
              )}
              {item.items === undefined ? null : (
                <ChevronRight
                  className={styles.subGlyph}
                  size={14}
                  strokeWidth={1.5}
                  aria-hidden
                />
              )}
            </button>
          ),
        )}
      </div>

      {branch?.action.items === undefined ? null : (
        <MenuPopup
          label={branch.action.label}
          items={branch.action.items}
          // Hung off the item's right edge, one gap over, and flipped by `place` when the
          // window runs out on that side.
          anchor={{
            kind: "point",
            x: branch.rect.right + GAP,
            y: branch.rect.top,
          }}
          nested
          autoFocus={branch.focus}
          // The reason is the whole difference between leaving a submenu and finishing with
          // the menu: `dismissed` asks for the parent item back, while `chosen` and `moved-on`
          // end the act at every level — and a parent list left standing over the drawer that
          // item just opened would be the bug decision 4 exists to prevent.
          onClose={(reason) => {
            setSub(null);
            if (reason === "dismissed") focusAt(here(drawn, branch.action.id));
            else onClose(reason);
          }}
        />
      )}
    </>,
    document.body,
  );
}

/** Which reachable item a given id is, so closing a submenu can put focus back on its parent. */
function here(drawn: readonly MenuItem[], id: string): number {
  return drawn
    .filter((one): one is MenuAction => !isSeparator(one) && one.disabled !== true)
    .findIndex((one) => one.id === id);
}
